import React from 'react';
import { CreditCard } from 'lucide-react';

/**
 * Account credits toggle + applied-amount preview.
 *
 * Props:
 *  - userCredits (number, AUD)         - user's current balance
 *  - applyCredits (bool)               - toggle state
 *  - onToggle (fn)                     - toggle handler
 *  - amountToApply (number, AUD)       - amount that will actually be applied
 *  - formatCredits (fn)                - currency-aware formatter (AUD credits)
 */
export const CheckoutCreditsSection = ({
  userCredits,
  applyCredits,
  onToggle,
  amountToApply,
  formatCredits,
}) => {
  if (!userCredits || userCredits <= 0) return null;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm" data-testid="credits-section">
      <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
        <CreditCard size={20} /> Account Credits
      </h2>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 dark:text-neutral-400">Available Balance</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="credits-balance">{formatCredits(userCredits)}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={applyCredits}
            onChange={onToggle}
            className="sr-only peer"
            data-testid="apply-credits-toggle"
          />
          <div className="w-14 h-7 bg-gray-200 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-emerald-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-700 dark:text-neutral-300">{applyCredits ? 'Applied' : 'Apply'}</span>
        </label>
      </div>

      {applyCredits && (
        <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl border border-emerald-100">
          <p className="text-sm text-emerald-800">
            <span className="font-semibold">{formatCredits(amountToApply)}</span> will be applied to this order
          </p>
        </div>
      )}
    </div>
  );
};
